import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { InsuranceTabStore, InsuranceTabUtilsTypes } from '@frontend/insurance-tab';
import { theme } from '@frontend/theme';
import { SelectionFilter } from './SelectionFilter';

export const SelectionFilterContainer = () => {
  const { t } = useTranslation('scheduleCalendarActions');

  const {
    isLoading,
    insuranceStatusCounts,
    currentStatusSelection,
    currentFilterValue,
    setCurrentStatusSelection,
    setCurrentFilterValue,
  } = InsuranceTabStore.useInsuranceTabShallowStore(
    'setCurrentFilterValue',
    'setCurrentStatusSelection',
    'currentStatusSelection',
    'insuranceStatusCounts',
    'isLoading',
    'currentFilterValue'
  );

  // Sync the currentFilterValue to currentStatusSelection when currentFilterValue changes externally
  useEffect(() => {
    if (currentFilterValue && currentStatusSelection !== currentFilterValue) {
      setCurrentStatusSelection(currentFilterValue);
    }
  }, [currentFilterValue, currentStatusSelection, setCurrentStatusSelection]);

  const handleStatusSelection = (status: string) => {
    if (status !== currentStatusSelection) {
      setCurrentStatusSelection(status);
      setCurrentFilterValue(status);
    } else {
      setCurrentStatusSelection('');
      setCurrentFilterValue('');
    }
  };

  const { verifiedCount, unverifiedCount, errorCount } = useMemo(
    () => ({
      verifiedCount:
        (insuranceStatusCounts?.[InsuranceTabUtilsTypes.StatusSelectionLabel.AutoVerified] || 0) +
        (insuranceStatusCounts?.[InsuranceTabUtilsTypes.StatusSelectionLabel.ManualVerified] || 0),
      unverifiedCount: insuranceStatusCounts?.[InsuranceTabUtilsTypes.StatusSelectionLabel.Unverified] || 0,
      errorCount: insuranceStatusCounts?.[InsuranceTabUtilsTypes.StatusSelectionLabel.Error] || 0,
    }),
    [insuranceStatusCounts]
  );

  return (
    <div css={selectionFilterContainerStyles}>
      <SelectionFilter
        label={t('Verified')}
        iconName='shield'
        iconColor={theme.colors.success30}
        value={verifiedCount.toString()}
        isSelected={currentStatusSelection === InsuranceTabUtilsTypes.StatusSelection.Verified}
        onClick={() => handleStatusSelection(InsuranceTabUtilsTypes.StatusSelection.Verified)}
        isLoading={isLoading}
      />
      <SelectionFilter
        label={t('Unverified')}
        iconName='insurance-incomplete'
        value={unverifiedCount.toString()}
        iconColor={theme.colors.warning30}
        isSelected={currentStatusSelection === InsuranceTabUtilsTypes.StatusSelection.Unverified}
        onClick={() => handleStatusSelection(InsuranceTabUtilsTypes.StatusSelection.Unverified)}
        isLoading={isLoading}
      />
      <SelectionFilter
        label={t('Error')}
        iconName='insurance-error'
        value={errorCount.toString()}
        iconColor={theme.colors.critical50}
        isSelected={currentStatusSelection === InsuranceTabUtilsTypes.StatusSelection.Error}
        onClick={() => handleStatusSelection(InsuranceTabUtilsTypes.StatusSelection.Error)}
        isLoading={isLoading}
      />
    </div>
  );
};

const selectionFilterContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  margin: theme.spacing(3, 0),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'space-between',
});
